// DateRangePickerModal.js
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormHelperText
} from '@mui/material';
import { DateRangePicker } from 'react-date-range';
import { format, addDays } from 'date-fns';
import CloseIcon from '@mui/icons-material/Close';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Divider } from '@mui/material';
import { notify } from "../../bmr/utils/methods/notification";
import { sortDatesAsc} from "../../bmr/utils/generic/function"

const DateRangePickerModal = ({ type = 'date', open, onClose, onApply, value, minDate, maxDate }) => {
  const [dateRange, setDateRange] = useState({
    startDate: value?.startDate || new Date(),
    endDate: value?.endDate || new Date(),
    key: 'selection'
  });
  
  const [mode, setMode] = useState(type);
  const [selectedDates, setSelectedDates] = useState(value);

  // Automatically set mode when type prop changes
  useEffect(() => {
    setMode(type);
  }, [type]);

  // Reset mode when modal opens
  useEffect(() => {
    if (open) {
      setMode(type);
    }
  }, [open, type]);

  const handleDateSelect = (ranges) => {
    setDateRange(ranges.selection);
  };

  const handleDateChange = (event) => {
    const selected = event.target.value ? sortDatesAsc(event.target.value): event.target.value;
    
    // Limit selection to 5 dates
    if (selected.length > 5) {
        notify.error(`You can only select up to 5 preferred days.`);
      return;
    }
    console.log('selected>>',selected)
    setSelectedDates(selected);
  };

  const handleApply = () => {
    if (mode === 'date') {
      setMode('apheresis'); // Change to apheresis mode for next step
      onApply({
        selectedDates: [],
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      setSelectedDates([])
    } else {
      onApply({
        selectedDates: selectedDates,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      onClose();
    }
  };

  // Generate dates between start and end date for apheresis selection
  const generateDateOptions = () => {
    if (!dateRange.startDate || !dateRange.endDate) return [];
    
    const dates = [];
    let currentDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    while (currentDate <= endDate) {
      dates.push(format(currentDate, 'dd/MM/yyyy'));
      currentDate = addDays(currentDate, 1);
    }
    
    return dates;
  };

const handleBack = () => {
    setMode('date');
  };

  const renderApheresisContent = () => {
    const dateOptions = generateDateOptions();
    
    return (
      <>
        {/* Selected Range Display */}
        {dateRange.startDate && dateRange.endDate && (
            <p className="bg-[#E8EFFC] text-[#1958EB] font-normal px-3 py-2 rounded-md w-full">
              Apheresis Date Range: <span className="font-bold">{format(dateRange.startDate, 'dd/MM/yyyy')} - {format(dateRange.endDate, 'dd/MM/yyyy')}</span>
            </p>
        )}

        {/* Multi-select for preferred dates */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight="normal">
            Select Preferred Dates
          </Typography>
          <FormControl fullWidth>
         
            <Select
              multiple
              displayEmpty
              value={selectedDates}
              onChange={handleDateChange}
              renderValue={(selected) =>
                selected.length === 0 ? "--Select--" : selected.join(", ")
              }
              sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #e5e7eb !important',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #bdbdbd !important',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: '1px solid #e5e7eb !important',
              }
            }}
            >
              {dateOptions?.map((date) => (
                <MenuItem key={date} value={date}>
                  <Checkbox checked={selectedDates.includes(date)} />
                  <ListItemText primary={date} />
                </MenuItem>
              ))}
            </Select>

            {/* Orange Note */}
            <FormHelperText sx={{ color: "#FF9500", mt: 1 }}>
              Note: You have to select 5 preferred days. {selectedDates.length}/5 selected
            </FormHelperText>
          </FormControl>
        </Box>
      </>
    );
  };

  const renderDateContent = () => {
    return (
      <>
        <Box sx={{ mt: 2 }}>
          <DateRangePicker
            ranges={[dateRange]}
            onChange={handleDateSelect}
            minDate={minDate}
            maxDate={maxDate}
            months={2}
            direction="horizontal"
            showMonthAndYearPickers={true}
            rangeColors={['#1976d2']}
          />
        </Box>
        {dateRange.startDate && dateRange.endDate && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="body2">
              Selected Range: {format(dateRange.startDate, 'dd/MM/yyyy')} - {format(dateRange.endDate, 'dd/MM/yyyy')}
            </Typography>
          </Box>
        )}
      </>
    );
  };

  const renderContent = () => {
    switch (mode) {
      case 'date':
        return renderDateContent();
      
      case 'apheresis':
        return renderApheresisContent();
      
      default:
        return null;
    }
  };

  const isApplyDisabled = () => {
    if (mode === 'date') {
      return !dateRange.startDate || !dateRange.endDate;
    } else {
      return selectedDates?.length != 5;
    }
  };

  const getDialogTitle = () => {
    switch (mode) {
      case 'date':
        return 'Select Date Range';
      case 'apheresis':
        return 'Select Preferred Apheresis Dates';
      default:
        return 'Select Date Range';
    }
  };

  const getButtonText = () => {
    switch (mode) {
      case 'date':
        return 'Next';
      case 'apheresis':
        return 'Done';
      default:
        return 'Apply';
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
            {mode !== 'date' && <IconButton 
            onClick={handleBack}
            sx={{ mr: 1 }}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>}
          <Typography variant="h6">
            {getDialogTitle()}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ mt: 1 }} />

      </DialogTitle>
      
      <DialogContent>
        {renderContent()}
      </DialogContent>
      
      <DialogActions sx={{ p: 3 }}>
        <Box flex={1} />
        <Button 
          onClick={handleApply} 
          variant="contained"
          disabled={isApplyDisabled()}
        >
          {getButtonText()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DateRangePickerModal;